import { MilkDomainService } from './domain';
import { MilkFactory } from './entity/milkFactory';

import type { MilkEntity } from './entity/milkEntity';
import type { MilkIRepository } from './entity/milkIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { MilkJson, MilkJsonPvt } from '@/types/domain/milk/entities/milkEntity';

import { repository } from '@/share';

/**
 * ミルクアプリケーションサービス
 */
export class MilkApplicationService {
  private service!: MilkDomainService;

  private milkFactory = new MilkFactory();

  /**
   * Injection
   * @param milkRepository milkRepository
   * @param logger logger
   */
  public inject(
    milkRepository: MilkIRepository,
    logger: Logger,
  ) {
    this.service = new MilkDomainService(logger);
    this.service.inject(milkRepository);
  }

  /**
   * ミルク取得
   * @param token トークン
   * @returns 一覧
   */
  async getMilkList(token: string | null) {
    const result = await repository.milk.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: MilkEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * 指定日のミルクリスト取得
   * @param date 日付
   * @returns リスト
   */
  async getMilkListWithDate(date: string) {
    const result = await this.service.getMilkListWithDate(date);
    return result;
  }

  /**
   * ミルク作成
   * @param input 入力
   * @returns 結果
   */
  async createMilk(
    input: MilkJson,
  ) {
    const entity = this.milkFactory.createFromJson(input);
    const result = await repository.milk.create(entity);
    return result;
  }

  /**
   * ミルク更新
   * @param input 入力
   * @returns 結果
   */
  async updateMilk(
    input: MilkJson,
  ) {
    const entity = this.milkFactory.createFromJson(input);
    const result = await repository.milk.update(entity);
    return result;
  }

  /**
   * ミルク削除
   * @param input 入力
   * @returns 結果
   */
  async deleteMilk(
    input: MilkJson,
  ) {
    const entity = this.milkFactory.createFromJson(input);
    const result = await repository.milk.delete(entity);
    return result;
  }

  /**
   * ミルクリストを更新する(Create)
   * @param milkList ミルクリスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createMilkStore(
    milkList: MilkJson[],
    payload: EntitySubscribeType,
    filter: ((json: MilkJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      milkList,
      payload,
      this.milkFactory,
      filter,
    );
  }

  /**
   * ミルクリストを更新する(Update)
   * @param milkList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateMilkStore(
    milkList: MilkJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      milkList,
      payload,
      this.milkFactory,
    );
  }

  /**
   * ミルクリストを更新する(Delete)
   * @param milkList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteMilkStore(
    milkList: MilkJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      milkList,
      payload,
      this.milkFactory,
    );
  }
}
